import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../services/api';
import { Mail, Building2, Briefcase, UserCheck, Pencil, X, Check, CreditCard, Cake, CalendarDays, Hash } from 'lucide-react';
import { IDCard, deptColor } from './IDCardPage';

const designations = [
  "Junior Analyst","Senior Analyst","Data Head","Manager","Associate Manager",
  "Senior Project Manager - Field Operations","Associate Director - Insights",
  "Manager – Project Management and Operations","Partner - Insights & Strategy",
  "Associate Manager – Insights","Senior Director - Insights",
  "Senior Executive - Data Processing","Senior Research Executive - Insights",
  "Senior Manager - Insights","Project Manager","Junior Analyst - Data Processing",
  "Senior Consultant","Senior Project Manager","Full-Time Consultant",
  "Chief Executive Officer","Director - Insights","Head of Research & Client Servicing",
  "Manager - Insights","Senior Data Processing Executive","Head of Data Operations",
  "Senior Manager-Project Management and Operations","Manager – Data Processing",
  "Senior Manager - Finance",
];

const avatarColor = (name) => {
  const colors = ['#4f46e5','#0891b2','#059669','#d97706','#dc2626','#7c3aed','#db2777','#0284c7'];
  if (!name) return colors[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % colors.length;
  return colors[Math.abs(h)];
};

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ width: 34, height: 34, borderRadius: 9, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={15} color="#64748b" />
      </div>
      <div>
        <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#94a3b8', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{value || '—'}</div>
      </div>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#94a3b8', marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 9,
  fontSize: 13.5, color: '#1e293b', background: '#fff', fontFamily: 'inherit',
  outline: 'none', boxSizing: 'border-box',
};

const UserProfile = ({ userId }) => {
  const [profile, setProfile] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(userId);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ name: '', department: '', reporting_to: '', designation: '', date_of_birth: '', date_of_joining: '' });
  const [photoUploading, setPhotoUploading] = useState(false);
  const [showIdCard, setShowIdCard] = useState(false);

  const currentUserEmail = localStorage.getItem('username');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('users/user-list/');
        setUsers(response.data);
        const currentUser = response.data.find(user => user.email === currentUserEmail);
        if (currentUser) setSelectedUserId(currentUser.id);
      } catch { setError('Failed to fetch users'); }
    };
    fetchUsers();
  }, [currentUserEmail]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await api.get(`users/profile/update/${selectedUserId}/`);
        setProfile(response.data);
        setFormData({
          name: response.data.name || '',
          department: response.data.department || '',
          reporting_to: response.data.reporting_to?.map((r) => r.id) || [],
          designation: response.data.designation || '',
          date_of_birth: response.data.date_of_birth || '',
          date_of_joining: response.data.date_of_joining || '',
          employee_number: response.data.employee_number || '',
        });
      } catch (err) { setError(err.response?.data?.detail || 'An error occurred'); }
    };
    if (selectedUserId) fetchUserProfile();
  }, [selectedUserId]);

  const handleUserChange = (e) => { setSelectedUserId(e.target.value); setProfile(null); setError(null); setIsEditing(false); };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === 'reporting_to' ? [parseInt(value)] : value }));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        date_of_birth: formData.date_of_birth || null,
        date_of_joining: formData.date_of_joining || null,
        employee_number: formData.employee_number || null,
      };
      const response = await api.put(`users/profile/update/${selectedUserId}/`, payload);
      setProfile(prev => ({ ...prev, ...response.data, date_of_birth: payload.date_of_birth, date_of_joining: payload.date_of_joining, employee_number: payload.employee_number }));
      setIsEditing(false);
    } catch (err) { setError(err.response?.data?.detail || 'An error occurred'); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      if (selectedUserId) fd.append('user_id', selectedUserId);
      const token = localStorage.getItem('Token');
      const res = await api.post('users/upload-photo/', fd, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Token ${token}` },
      });
      setProfile(prev => ({ ...prev, photo_url: res.data.photo_url }));
    } catch { setError('Photo upload failed.'); }
    finally { setPhotoUploading(false); }
  };

  const initials = (name) => (name || '?').split(' ').slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('');
  const reportingTo = profile?.reporting_to?.map(r => r.name).join(', ') || '—';

  const idCardEmp = profile ? {
    id: selectedUserId,
    email: profile.user?.email || '',
    name: profile.name || '',
    department: profile.department || '',
    designation: profile.designation || '',
    photo_url: profile.photo_url || null,
    date_of_birth: profile.date_of_birth || null,
    date_of_joining: profile.date_of_joining || null,
    employee_number: profile.employee_number || '',
  } : null;

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '40px 16px' }}>
      <div style={{ maxWidth: showIdCard ? 960 : 560, margin: '0 auto', transition: 'max-width 0.25s ease' }}>

        {/* User selector — admin only */}
        {currentUserEmail === 'ram@4sight-global.com' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>Viewing profile for</span>
            <select value={selectedUserId} onChange={handleUserChange}
              style={{ ...inputStyle, flex: 1, padding: '7px 12px', fontSize: 13 }}>
              <option value="">— Select a user —</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
              ))}
            </select>
          </div>
        )}

        {!profile && !error && (
          <div style={{ textAlign: 'center', color: '#94a3b8', paddingTop: 60, fontSize: 14 }}>Loading…</div>
        )}

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', color: '#dc2626', fontSize: 13, marginBottom: 16 }}>
            {error}
          </div>
        )}

        {/* Main row: profile card + ID card side by side */}
        <div style={{ display: 'flex', gap: 28, alignItems: 'flex-start' }}>

          {/* Left: profile / edit card */}
          <div style={{ flex: '1 1 0', minWidth: 0 }}>

        {profile && !isEditing && (
          <div style={{ background: '#fff', borderRadius: 18, border: '1px solid #e2e8f0', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '24px 24px 20px', borderBottom: '1px solid #f1f5f9' }}>
              {profile.photo_url ? (
                <img src={profile.photo_url} alt={profile.name}
                  style={{ width: 60, height: 60, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `3px solid ${avatarColor(profile.name)}` }} />
              ) : (
                <div style={{ width: 60, height: 60, borderRadius: '50%', background: avatarColor(profile.name), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                  {initials(profile.name)}
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{profile.name || '—'}</div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{profile.designation || ''}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setShowIdCard(v => !v); setIsEditing(false); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: showIdCard ? '#0f172a' : '#f1f5f9', color: showIdCard ? '#fff' : '#475569', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  <CreditCard size={13} />
                  ID Card
                </button>
                <button onClick={() => { setIsEditing(true); setShowIdCard(false); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  <Pencil size={13} />
                  Edit
                </button>
              </div>
            </div>

            {/* Detail rows */}
            <div style={{ padding: '0 24px 8px' }}>
              <DetailRow icon={Mail} label="Email" value={profile.user?.email} />
              <DetailRow icon={Building2} label="Department" value={profile.department} />
              <DetailRow icon={Briefcase} label="Designation" value={profile.designation} />
              <DetailRow icon={UserCheck} label="Reporting To" value={reportingTo} />
              {profile.date_of_birth && <DetailRow icon={Cake} label="Date of Birth" value={new Date(profile.date_of_birth).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} />}
              {profile.date_of_joining && <DetailRow icon={CalendarDays} label="Date of Joining" value={new Date(profile.date_of_joining).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} />}
              {profile.employee_number && currentUserEmail === 'ram@4sight-global.com' && <DetailRow icon={Hash} label="Employee Number" value={profile.employee_number} />}
            </div>
          </div>
        )}

        {profile && isEditing && (
          <div style={{ background: '#fff', borderRadius: 18, border: '1px solid #e2e8f0', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            {/* Edit header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 24px', borderBottom: '1px solid #f1f5f9' }}>
              <label style={{ cursor: 'pointer', flexShrink: 0, position: 'relative' }} title="Click to upload photo">
                {profile.photo_url ? (
                  <img src={profile.photo_url} alt={profile.name}
                    style={{ width: 60, height: 60, borderRadius: '50%', objectFit: 'cover', border: `3px solid ${avatarColor(profile.name)}`, display: 'block' }} />
                ) : (
                  <div style={{ width: 60, height: 60, borderRadius: '50%', background: avatarColor(profile.name), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: '#fff' }}>
                    {initials(profile.name)}
                  </div>
                )}
                <div style={{ position: 'absolute', bottom: 0, right: 0, width: 20, height: 20, borderRadius: '50%', background: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', border: '2px solid #fff' }}>
                  {photoUploading ? '…' : '📷'}
                </div>
                <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} disabled={photoUploading} />
              </label>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Edit Profile</div>
                <div style={{ fontSize: 12.5, color: '#94a3b8', marginTop: 2 }}>{profile.user?.email}</div>
              </div>
              <button onClick={() => setIsEditing(false)}
                style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                <X size={15} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ padding: '20px 24px' }}>
              <FieldRow label="Name">
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} style={inputStyle} />
              </FieldRow>
              <FieldRow label="Department">
                <select name="department" value={formData.department} onChange={handleInputChange} style={inputStyle}>
                  <option value="">— Select department —</option>
                  <option value="Accounts">Accounts</option>
                  <option value="Data Processing">Data Processing</option>
                  <option value="Operations">Operations</option>
                  <option value="Research">Research</option>
                </select>
              </FieldRow>
              <FieldRow label="Designation">
                <select name="designation" value={formData.designation} onChange={handleInputChange} style={inputStyle}>
                  <option value="">— Select designation —</option>
                  {designations.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="Reporting To">
                <select name="reporting_to" value={formData.reporting_to[0] || ''} onChange={handleInputChange} style={inputStyle}>
                  <option value="">— Select person —</option>
                  {users.filter(u => u.id !== Number(selectedUserId)).map(u => (
                    <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
                  ))}
                </select>
              </FieldRow>
              {currentUserEmail === 'ram@4sight-global.com' && (
                <FieldRow label="Employee Number">
                  <input type="text" name="employee_number" value={formData.employee_number || ''} onChange={handleInputChange} placeholder="e.g. EMP-001" style={inputStyle} />
                </FieldRow>
              )}
              <FieldRow label="Date of Birth">
                <input type="date" name="date_of_birth" value={formData.date_of_birth || ''} onChange={handleInputChange} style={inputStyle} />
              </FieldRow>
              <FieldRow label="Date of Joining">
                <input type="date" name="date_of_joining" value={formData.date_of_joining || ''} onChange={handleInputChange} style={inputStyle} />
              </FieldRow>
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button type="submit"
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 0', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 9, fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  <Check size={14} /> Save Changes
                </button>
                <button type="button" onClick={() => setIsEditing(false)}
                  style={{ padding: '10px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 9, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

          </div>{/* end left column */}

          {/* Right: ID Card */}
          {showIdCard && idCardEmp && !isEditing && (
            <div style={{ flexShrink: 0 }}>
              <IDCard employee={idCardEmp} />
              {(!idCardEmp.employee_number || !idCardEmp.photo_url || !idCardEmp.date_of_birth || !idCardEmp.date_of_joining) && currentUserEmail !== 'ram@4sight-global.com' && (
                <div style={{ marginTop: 12, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#9a3412', maxWidth: 340 }}>
                  ℹ️ Some details are missing. Please contact your admin to complete your profile.
                </div>
              )}
              {currentUserEmail === 'ram@4sight-global.com' && (
                <button
                  onClick={() => {
                    const emp = idCardEmp;
                    const empId = emp.employee_number || `EMP-${String(selectedUserId).padStart(4, '0')}`;
                    const photoSrc = emp.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(emp.name || '?')}&background=1e295c&color=fff&size=256&bold=true`;
                    const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
                    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>ID Card – ${emp.name}</title><link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap" rel="stylesheet"><style>*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;box-sizing:border-box;margin:0;padding:0}body{display:flex;justify-content:center;align-items:center;min-height:100vh;background:#e2e6ec;font-family:'Poppins',system-ui,sans-serif}.card{position:relative;width:330px;border-radius:24px;overflow:hidden;background:#fff;box-shadow:0 25px 50px -12px rgba(15,23,42,0.35)}.top{background:radial-gradient(circle at 50% 0%,#1e295c 0%,#0a1128 75%);padding:22px 20px 28px;text-align:center;color:#fff}.hdr{display:flex;align-items:center;gap:8px}.logo-mark{width:26px;height:26px;border-radius:6px;background:linear-gradient(135deg,#2dd4bf,#4f46e5);display:flex;align-items:center;justify-content:center;flex-shrink:0}.co{font-size:11px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;text-align:left;color:rgba(255,255,255,0.9)}.gold-line{height:2px;margin:12px -20px 0;background:linear-gradient(90deg,transparent,#f6e17a 20%,#fff3c4 50%,#f6e17a 80%,transparent)}.ring{width:124px;height:124px;margin:22px auto 0;border-radius:50%;padding:5px;background:conic-gradient(from 180deg at 50% 50%,#b8860b,#ffd700,#fff8dc,#daa520,#8b6508,#ffd700,#b8860b);box-shadow:0 10px 25px rgba(0,0,0,0.4)}.ring img{width:100%;height:100%;border-radius:50%;object-fit:cover;display:block;border:2px solid #0a1128}.ename{margin:14px 0 2px;font-size:21px;font-weight:800;color:#fff}.role{font-size:12px;font-weight:500;color:rgba(255,255,255,0.7);margin:0 0 14px}.dept{display:inline-block;padding:6px 20px;border-radius:99px;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#1e1e1e;background:linear-gradient(180deg,#fff 0%,#e2e8f0 50%,#cbd5e1 100%);box-shadow:0 4px 10px rgba(0,0,0,0.2),inset 0 1px 1px rgba(255,255,255,0.9)}.bottom{padding:24px 20px 20px;background:linear-gradient(135deg,#f1f5f9 0%,#cbd5e1 100%);display:flex;flex-direction:column;gap:14px}.row{display:flex;align-items:center;gap:12px}.badge{width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:15px}.rt{font-size:13px;font-weight:600;color:#1e293b;word-break:break-all}.footer{background:linear-gradient(180deg,#cbd5e1 0%,#94a3b8 100%);padding:12px 16px;display:flex;align-items:center;justify-content:space-between;border-top:1px solid rgba(255,255,255,0.4)}.ft{font-size:10px;font-weight:700;letter-spacing:.05em;color:#334155;text-transform:uppercase}.seal{width:44px;height:44px;margin-top:-26px;border-radius:14px;background:linear-gradient(160deg,#24356b,#0a1128);display:flex;align-items:center;justify-content:center;box-shadow:0 4px 8px rgba(0,0,0,0.3)}@media print{body{background:white}.card{box-shadow:none}}</style></head><body><div class="card"><div class="top"><div class="hdr"><div class="logo-mark"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg></div><div class="co">4SIGHT RESEARCH &amp; ANALYTICS FZCO</div></div><div class="gold-line"></div><div class="ring"><img src="${photoSrc}" alt="${emp.name}"></div><h2 class="ename">${emp.name}</h2><p class="role">${emp.designation || '—'}</p>${emp.department ? `<span class="dept">${emp.department.toUpperCase()}</span>` : ''}</div><div class="bottom"><div class="row"><div class="badge" style="background:#dbeafe">✉️</div><div class="rt">${emp.email}</div></div><div class="row"><div class="badge" style="background:#e0e7ff">🪪</div><div class="rt">${empId}</div></div>${fmtDate(emp.date_of_birth) ? `<div class="row"><div class="badge" style="background:#fef3c7">🎂</div><div class="rt">${fmtDate(emp.date_of_birth)}</div></div>` : ''}${fmtDate(emp.date_of_joining) ? `<div class="row"><div class="badge" style="background:#ede9fe">📅</div><div class="rt">${fmtDate(emp.date_of_joining)}</div></div>` : ''}</div><div class="footer"><span class="ft">4sightglobal.com</span><div class="seal"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="1.6" stroke-linecap="round"><path d="M3 21V12a9 9 0 0 1 18 0v9"/><path d="M7 21v-7a5 5 0 0 1 10 0v7"/></svg></div><span class="ft">Employee ID</span></div></div><script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();}<\/script></body></html>`;
                    const win = window.open('', '_blank', 'width=500,height=750');
                    win.document.write(html); win.document.close();
                  }}
                  style={{ marginTop: 12, width: '100%', maxWidth: 340, padding: '10px 20px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  🖨️ Print / Save as PDF
                </button>
              )}
            </div>
          )}

        </div>{/* end flex row */}
      </div>
    </div>
  );
};

export default UserProfile;
